import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { alpha } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Slider from '@mui/material/Slider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GridOnIcon from '@mui/icons-material/GridOn';
import StraightenIcon from '@mui/icons-material/Straighten';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SelectField from '../UI/SelectField/SelectField';
import CometSpinner from '../UI/Progress/CometSpinner';
import { DeviceHelper } from '../../helpers/deviceHelper';

// Darkness below this rarely marks direct thermal stock (Zebra default is ~10–20)
const LOW_DARKNESS = 8;
const MAX_DARKNESS = 30;
const SUGGESTED_DARKNESS = 20;
const LABEL_TOP_LIMIT = 40;
const LEFT_POSITION_LIMIT = 50;
// Sensed label length may differ from the template by this much before we flag it
const LENGTH_TOLERANCE = 0.15;

const SEVERITY_ICON = {
  success: CheckCircleIcon,
  info: InfoOutlinedIcon,
  warning: WarningAmberIcon,
  error: ErrorOutlineIcon,
};
const SEVERITY_RANK = { error: 3, warning: 2, info: 1, success: 0 };

const isTransfer = (settings, details) => /trans/i.test(settings.printMethod || '') || details.thermalTransfer === true;
const hasOffsets = (settings) =>
  (parseInt(settings.labelTop, 10) || 0) !== 0 || (parseInt(settings.leftPosition, 10) || 0) !== 0;

// What in the printer's stored settings would stop a label printing. Each finding is
// { severity, key, params, fix } — fix names the action offered next to it.
const analyse = ({ settings, hostStatus }, templates) => {
  const findings = [];
  const details = hostStatus?.details || {};
  (hostStatus?.errors || []).forEach(err => findings.push({ severity: 'error', key: 'diagPrinterError', params: { error: err } }));

  if (settings.language && !/zpl/i.test(settings.language)) {
    findings.push({ severity: 'error', key: 'diagLanguageNotZpl', params: { language: settings.language } });
  }
  if (isTransfer(settings, details)) {
    findings.push(details.ribbonOut
      ? { severity: 'error', key: 'diagTransferNoRibbon', fix: 'directThermal' }
      : { severity: 'info', key: 'diagTransferMode', fix: 'directThermal' });
  }
  const darkness = parseFloat(settings.darkness);
  if (!isNaN(darkness) && darkness < LOW_DARKNESS) {
    findings.push({ severity: 'warning', key: 'diagDarknessLow', params: { darkness }, fix: 'darkness' });
  }
  const labelTop = parseInt(settings.labelTop, 10);
  const leftPosition = parseInt(settings.leftPosition, 10);
  if ((!isNaN(labelTop) && Math.abs(labelTop) > LABEL_TOP_LIMIT) ||
      (!isNaN(leftPosition) && Math.abs(leftPosition) > LEFT_POSITION_LIMIT)) {
    findings.push({ severity: 'warning', key: 'diagOffsets', params: { top: settings.labelTop ?? 0, left: settings.leftPosition ?? 0 }, fix: 'offsets' });
  }
  const printWidth = parseInt(settings.printWidth, 10);
  templates.forEach(tpl => {
    if (!isNaN(printWidth) && tpl.labelWidth > printWidth + 8) {
      findings.push({ severity: 'warning', key: 'diagPrintWidth', params: { name: tpl.name, template: tpl.labelWidth, printer: printWidth } });
    }
    const sensed = details.labelLengthDots;
    if (sensed > 0 && tpl.labelHeight > 0 && Math.abs(sensed - tpl.labelHeight) / tpl.labelHeight > LENGTH_TOLERANCE) {
      findings.push({ severity: 'warning', key: 'diagLabelLength', params: { name: tpl.name, template: tpl.labelHeight, printer: sensed }, fix: 'calibrate' });
    }
  });
  if (details.labelWaiting) findings.push({ severity: 'warning', key: 'diagLabelWaiting' });
  return findings.sort((a, b) => SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity]);
};

// The settings as tiles, each with the state it's in
const tilesFor = ({ settings, hostStatus }, templates) => {
  const details = hostStatus?.details || {};
  const darkness = parseFloat(settings.darkness);
  const printWidth = parseInt(settings.printWidth, 10);
  const widest = templates.reduce((max, tpl) => Math.max(max, tpl.labelWidth || 0), 0);
  const tallest = templates.find(tpl => tpl.labelHeight > 0)?.labelHeight;
  const sensed = details.labelLengthDots;
  return [
    { key: 'status', label: 'status', value: hostStatus?.status,
      state: hostStatus == null ? null : hostStatus.isReadyToPrint === false ? 'error' : 'success' },
    { key: 'printMethod', label: 'printMethod', value: settings.printMethod || (details.thermalTransfer != null ? (details.thermalTransfer ? 'thermal transfer' : 'direct thermal') : null),
      state: isTransfer(settings, details) ? (details.ribbonOut ? 'error' : 'info') : (settings.printMethod || details.thermalTransfer != null ? 'success' : null) },
    { key: 'darkness', label: 'darkness', value: settings.darkness, gauge: isNaN(darkness) ? null : darkness / MAX_DARKNESS,
      state: isNaN(darkness) ? null : darkness < LOW_DARKNESS ? 'warning' : 'success' },
    { key: 'language', label: 'printerLanguage', value: settings.language,
      state: settings.language ? (/zpl/i.test(settings.language) ? 'success' : 'error') : null },
    { key: 'printWidth', label: 'printWidth', value: settings.printWidth,
      state: isNaN(printWidth) || widest === 0 ? null : widest > printWidth + 8 ? 'warning' : 'success' },
    { key: 'labelLength', label: 'labelLength', value: sensed,
      state: !(sensed > 0) || !tallest ? null : Math.abs(sensed - tallest) / tallest > LENGTH_TOLERANCE ? 'warning' : 'success' },
    { key: 'offsets', label: 'diagOffsetsLabel', value: settings.labelTop != null || settings.leftPosition != null
        ? `${settings.labelTop ?? 0} / ${settings.leftPosition ?? 0}` : null,
      state: settings.labelTop == null && settings.leftPosition == null ? null
        : (Math.abs(parseInt(settings.labelTop, 10) || 0) > LABEL_TOP_LIMIT || Math.abs(parseInt(settings.leftPosition, 10) || 0) > LEFT_POSITION_LIMIT) ? 'warning' : 'success' },
    { key: 'speed', label: 'printSpeed', value: settings.speed, state: null },
    { key: 'mediaType', label: 'mediaType', value: settings.mediaType, state: null },
    { key: 'printMode', label: 'printMode', value: details.printMode,
      state: details.labelWaiting ? 'warning' : null },
  ];
};

const REPORT_ROWS = [
  ['model', 'model'], ['firmware', 'firmware'], ['language', 'printerLanguage'], ['printMethod', 'printMethod'],
  ['darkness', 'darkness'], ['speed', 'printSpeed'], ['mediaType', 'mediaType'], ['printWidth', 'printWidth'],
  ['labelTop', 'labelTop'], ['leftPosition', 'leftPosition'],
];

// One setting: caption, value, a coloured edge for its state
const Tile = ({ label, value, state, gauge, action }) => (
  <Box sx={{
    position: 'relative', p: 1, pl: 1.25, borderRadius: 1.5, minWidth: 0,
    border: 1, borderColor: 'divider', borderLeft: 3,
    borderLeftColor: state ? `${state}.main` : 'divider',
    bgcolor: (theme) => (state && state !== 'success' ? alpha(theme.palette[state].main, 0.06) : 'transparent'),
  }}>
    <Typography variant='caption' color='text.secondary' noWrap component='div'>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
        {value ?? '–'}
      </Typography>
      {action}
    </Box>
    {gauge != null &&
      <LinearProgress variant='determinate' value={Math.min(100, Math.max(0, gauge * 100))}
        color={state === 'warning' ? 'warning' : 'primary'} sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
    }
  </Box>
);

const Step = ({ number, title, action, children }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center',
        bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.75rem', fontWeight: 700,
      }}>{number}</Box>
      <Typography variant='subtitle2' sx={{ flex: 1 }}>{title}</Typography>
      {action}
    </Box>
    <Box sx={{ pl: { xs: 0, sm: 3.75 } }}>{children}</Box>
  </Box>
);

const DeviceDiagnostics = ({ device, templates, disabled, status }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dateFormat = useSelector(state => state.auth.user?.dateFormat);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null);
  const [darkness, setDarkness] = useState(SUGGESTED_DARKNESS);
  const [adjusting, setAdjusting] = useState(false);
  const [templateId, setTemplateId] = useState('');
  // Step 1's answer: null until the config label is out, then 'fine' | 'blank'
  const [configSent, setConfigSent] = useState(false);
  const [configOutcome, setConfigOutcome] = useState(null);

  // A different printer's readings mean nothing here
  useEffect(() => {
    setResult(null);
    setAdjusting(false);
    setTemplateId('');
    setConfigSent(false);
    setConfigOutcome(null);
  }, [device.uid]);

  const findings = useMemo(() => (result ? analyse(result, templates) : []), [result, templates]);
  const tiles = useMemo(() => (result ? tilesFor(result, templates) : []), [result, templates]);
  const worst = findings[0]?.severity;
  // The Device Manager's status poll got nothing back (ProdFix reports it as not ready, EvoProd as unknown)
  const notResponding = status != null && /no response|did not respond|timeout/i.test(status.status || '');

  const alert = useCallback((message, severity) => {
    dispatch({ type: 'CREATE_ALERT', payload: { message, title: t(severity === 'error' ? 'error' : 'success'), severity } });
  }, [dispatch, t]);

  // Every action talks to the same printer channel, so only one runs at a time
  const run = useCallback(async (name, action) => {
    setBusy(name);
    try {
      await action();
    } catch (err) {
      alert(err?.message || String(err), 'error');
    } finally {
      setBusy(null);
    }
  }, [alert]);

  const read = useCallback(async () => {
    const res = await DeviceHelper.getPrinterSettings(device);
    setResult(res);
    const tone = parseFloat(res.settings.darkness);
    if (!isNaN(tone)) setDarkness(tone);
    return res;
  }, [device]);

  const readSettings = useCallback(() => run('read', read), [run, read]);

  // Open the panel → read the settings, once per printer. Not for one that isn't
  // answering: that's a dozen seconds of timeouts; the refresh button still tries.
  useEffect(() => {
    if (expanded && result == null && !disabled && !notResponding && busy == null) readSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, disabled, device.uid]);

  // Change a stored printer setting, then read back so the tiles reflect it
  const apply = useCallback((name, changes) => run(name, async () => {
    for (const [sgdVar, value] of changes) await DeviceHelper.setVar(device, sgdVar, value);
    await read();
    alert(t('diagSettingApplied'), 'success');
  }), [run, read, device, alert, t]);

  const printConfig = useCallback(() => run('config', async () => {
    await DeviceHelper.printConfigLabel(device);
    setConfigSent(true);
    setConfigOutcome(null);
  }), [run, device]);

  const calibrate = useCallback(() => run('calibrate', async () => {
    await DeviceHelper.calibrate(device);
    alert(t('diagSent'), 'success');
  }), [run, device, alert, t]);

  const printPattern = useCallback(() => run('pattern', async () => {
    // Size to the printer when it says, else the first assigned template, else 3x2 @ 203
    const tpl = templates[0];
    const width = parseInt(result?.settings.printWidth, 10) || tpl?.labelWidth || 600;
    const height = result?.hostStatus?.details?.labelLengthDots || tpl?.labelHeight || 400;
    const caption = [result?.settings.model, result?.settings.darkness != null ? 'D' + result.settings.darkness : null,
      new Date().toLocaleString()].filter(Boolean).join('  ');
    await DeviceHelper.print(device, DeviceHelper.testPatternZpl({ width, height, caption }));
    alert(t('diagSent'), 'success');
  }), [run, device, templates, result, alert, t]);

  const printSample = useCallback(() => run('sample', async () => {
    // No record — the server fills in the template's sample values
    const { data } = await axios.post('/api/label', {
      templateId, count: 1, baseUrl: window.location.origin, dateFormat,
    });
    await DeviceHelper.print(device, data);
    alert(t('diagSent'), 'success');
  }), [run, device, templateId, dateFormat, alert, t]);

  const copyReport = useCallback(async () => {
    const lines = [`${t('printer')}: ${device.name} (${device.connection || ''} ${device.uid || ''})`];
    if (configOutcome) lines.push(`${t('diagConfigLabel')}: ${t(configOutcome === 'blank' ? 'diagPrintedBlank' : 'diagPrintedFine')}`);
    if (result) {
      REPORT_ROWS.forEach(([key, label]) => lines.push(`${t(label)}: ${result.settings[key] ?? '-'}`));
      const details = result.hostStatus?.details || {};
      lines.push(`${t('status')}: ${result.hostStatus?.status ?? '-'}`);
      lines.push(`${t('labelLength')}: ${details.labelLengthDots ?? '-'}`);
      lines.push(`${t('printMode')}: ${details.printMode ?? '-'}`);
      lines.push('');
      (findings.length > 0 ? findings : [{ severity: 'success', key: 'diagNoIssues' }])
        .forEach(f => lines.push(`[${f.severity}] ${t(f.key, f.params)}`));
    }
    templates.forEach(tpl => lines.push(`${t('template')}: ${tpl.name} ${tpl.labelWidth}x${tpl.labelHeight} @ ${tpl.dpi}`));
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      alert(t('copiedToClipboard'), 'success');
    } catch (err) {
      alert(err?.message || String(err), 'error');
    }
  }, [device, result, findings, templates, configOutcome, alert, t]);

  const off = disabled || busy != null;
  const spin = (name, icon) => (busy === name ? <CometSpinner stroke="inherit" size={16} /> : icon);
  const templateOptions = templates.map(tpl => ({ label: tpl.name, value: tpl._id }));
  const settings = result?.settings || {};
  const details = result?.hostStatus?.details || {};

  // The button that fixes a finding, beside it
  const fixButton = (fix) => {
    if (!result?.sgd && fix !== 'calibrate') return null;
    switch (fix) {
      case 'darkness':
        return <Button size='small' color='inherit' disabled={off} onClick={() => apply('darkness', [['print.tone', String(SUGGESTED_DARKNESS)]])}>
          {spin('darkness')}{t('diagSetDarknessTo', { value: SUGGESTED_DARKNESS })}
        </Button>;
      case 'directThermal':
        return <Button size='small' color='inherit' disabled={off} onClick={() => apply('method', [['ezpl.print_method', 'direct thermal']])}>
          {spin('method')}{t('diagSetDirectThermal')}
        </Button>;
      case 'offsets':
        return hasOffsets(settings)
          ? <Button size='small' color='inherit' disabled={off} onClick={() => apply('offsets', [['zpl.label_top', '0'], ['zpl.left_position', '0']])}>
            {spin('offsets')}{t('diagResetOffsets')}
          </Button>
          : null;
      case 'calibrate':
        return <Button size='small' color='inherit' disabled={off} onClick={calibrate}>{spin('calibrate')}{t('diagCalibrate')}</Button>;
      default:
        return null;
    }
  };

  // Header chip: the state at a glance without opening the panel
  const summary = configOutcome === 'blank'
    ? { color: 'error', label: t('diagSummaryMedia') }
    : result == null
      ? (notResponding ? { color: 'default', label: t('diagSummaryNoResponse') } : null)
      : findings.length === 0
        ? { color: 'success', label: t('diagSummaryOk') }
        : { color: worst === 'info' ? 'info' : worst, label: t('diagSummaryIssues', { count: findings.filter(f => f.severity !== 'info').length || findings.length }) };

  return (
    <Accordion disableGutters variant='outlined' expanded={expanded} onChange={(e, open) => setExpanded(open)}
      sx={{ borderRadius: 2, overflow: 'hidden', '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}
        sx={{ bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04), '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}>
        <TroubleshootIcon fontSize='small' color='primary' />
        <Typography variant='subtitle2' sx={{ flex: 1 }}>{t('diagnostics')}</Typography>
        {busy === 'read' && <CometSpinner stroke="inherit" size={16} />}
        {summary && <Chip size='small' color={summary.color} variant={summary.color === 'default' ? 'outlined' : 'filled'} label={summary.label} />}
      </AccordionSummary>

      <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {notResponding &&
          <Alert severity='info' variant='outlined'>{t('diagNotResponding')}</Alert>
        }

        {/* 1 — rules Evora out (or in) before anything else */}
        <Step number={1} title={t('diagStepConfig')}>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>{t('diagStepConfigHelp')}</Typography>
          <Button size='small' variant={configSent ? 'outlined' : 'contained'} disabled={off} onClick={printConfig}
            startIcon={spin('config', <ReceiptLongIcon />)} data-cy='diagConfigLabel'>
            {t('diagConfigLabel')}
          </Button>
          {configSent &&
            <Box sx={{ mt: 1.5, textAlign: 'center' }}>
              <Typography variant='body2' sx={{ mb: 1 }}>{t('diagHowDidItPrint')}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Chip label={t('diagPrintedFine')} clickable color='success' icon={<CheckCircleIcon />}
                  variant={configOutcome === 'fine' ? 'filled' : 'outlined'} onClick={() => setConfigOutcome('fine')} data-cy='diagPrintedFine' />
                <Chip label={t('diagPrintedBlank')} clickable color='error' icon={<ErrorOutlineIcon />}
                  variant={configOutcome === 'blank' ? 'filled' : 'outlined'} onClick={() => setConfigOutcome('blank')} data-cy='diagPrintedBlank' />
              </Box>
            </Box>
          }
          {configOutcome === 'blank' && <Alert severity='error' sx={{ mt: 1 }}>{t('diagVerdictMedia')}</Alert>}
          {configOutcome === 'fine' && <Alert severity='success' sx={{ mt: 1 }}>{t('diagVerdictFine')}</Alert>}
        </Step>

        {/* 2 — what the printer has stored, which our ZPL never overrides */}
        <Step number={2} title={t('diagStepSettings')}
          action={
            <Tooltip title={t('diagReadSettings')}>
              <span>
                <IconButton size='small' disabled={off} onClick={readSettings} data-cy='diagReadSettings'>
                  {spin('read', <RefreshIcon fontSize='small' />)}
                </IconButton>
              </span>
            </Tooltip>
          }>
          {result == null &&
            <Typography variant='caption' color='text.secondary'>
              {busy === 'read' ? t('checking') : disabled ? t('checking') : t('diagReadHint')}
            </Typography>
          }
          {result != null &&
            <>
              {(settings.model || settings.firmware) &&
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
                  {[settings.model, settings.firmware].filter(Boolean).join(' · ')}
                </Typography>
              }
              {!result.sgd &&
                <Alert severity='info' variant='outlined' sx={{ mb: 1 }}>{t('diagNoSgd')}</Alert>
              }
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
                {tiles.map(tile => (
                  <Tile key={tile.key} label={t(tile.label)} value={tile.value} state={tile.state} gauge={tile.gauge}
                    action={tile.key === 'darkness' && result.sgd
                      ? <Tooltip title={t('diagAdjust')}>
                        <IconButton size='small' sx={{ p: 0.25 }} onClick={() => setAdjusting(a => !a)} data-cy='diagAdjustDarkness'>
                          <TuneIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      : null} />
                ))}
              </Box>

              {adjusting &&
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, px: 1 }}>
                  <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>{t('darkness')}</Typography>
                  <Slider size='small' min={0} max={MAX_DARKNESS} step={1} value={darkness} valueLabelDisplay='auto'
                    marks={[{ value: LOW_DARKNESS }, { value: SUGGESTED_DARKNESS }]}
                    onChange={(e, v) => setDarkness(v)} disabled={off} data-cy='diagDarkness' />
                  <Button size='small' variant='outlined' disabled={off || darkness === parseFloat(settings.darkness)}
                    onClick={() => apply('darkness', [['print.tone', String(darkness)]])} sx={{ flexShrink: 0 }} data-cy='diagSetDarkness'>
                    {spin('darkness')}{t('diagSetDarkness')}
                  </Button>
                </Box>
              }

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1.5 }}>
                {findings.length === 0 &&
                  <Alert severity='success' icon={<CheckCircleIcon fontSize='inherit' />}>{t('diagNoIssues')}</Alert>
                }
                {findings.map((f, index) => {
                  const Icon = SEVERITY_ICON[f.severity];
                  return (
                    <Alert key={'finding-' + index} severity={f.severity} icon={<Icon fontSize='inherit' />}
                      action={f.fix ? fixButton(f.fix) : null}
                      sx={{ py: 0, alignItems: 'center', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                      {t(f.key, f.params)}
                    </Alert>
                  );
                })}
              </Box>
            </>
          }
        </Step>

        {/* 3 — through our own ZPL, once the printer itself is known good */}
        <Step number={3} title={t('diagStepTest')}>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>{t('diagStepTestHelp')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: templateOptions.length > 0 ? 1.5 : 0 }}>
            <Button size='small' variant='outlined' disabled={off} onClick={printPattern}
              startIcon={spin('pattern', <GridOnIcon />)} data-cy='diagTestPattern'>
              {t('diagTestPattern')}
            </Button>
            <Button size='small' variant='outlined' disabled={off} onClick={calibrate}
              startIcon={spin('calibrate', <StraightenIcon />)} data-cy='diagCalibrate'>
              {t('diagCalibrate')}
            </Button>
          </Box>
          {templateOptions.length > 0 &&
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SelectField label={t('template')} name='diagTemplate' options={templateOptions}
                  value={templateId} onChange={(e) => setTemplateId(e.target.value)} fullWidth data-cy='diagTemplate' />
              </Box>
              <Button size='small' variant='outlined' disabled={off || !templateId} onClick={printSample}
                sx={{ flexShrink: 0 }} data-cy='diagPrintSample'>
                {spin('sample')}{t('diagPrintSample')}
              </Button>
            </Box>
          }
        </Step>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary'>
            {details.labelLengthDots > 0 || settings.printWidth ? `${settings.printWidth ?? '?'} × ${details.labelLengthDots ?? '?'} ${t('dots')}` : ''}
          </Typography>
          <Button size='small' variant='text' disabled={busy != null} onClick={copyReport}
            startIcon={<ContentCopyIcon />} data-cy='diagCopyReport'>
            {t('diagCopyReport')}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default DeviceDiagnostics;
